import type { TypedDocumentNode } from "@apollo/client";
import type { InputMetadata } from "@charterindex/halyard";
import type { ApiService } from "../services/api/api-service";
import type { CrudOperationConfig, CrudOperationResult, CrudSubmitContext } from "./crud-operation";
import type { JSX } from "react";
import { createElement } from "react";
import { Editor } from "../editor/editor";
import { resourceId, type ResourceIdentifier } from "../util/id";
import { CrudOperation } from "./crud-operation";


type UpdateOperationMutationVariables<Attributes> = { id: string, attributes: Attributes };
export type UpdateOperationViewProps<Attributes> = {
	id: string;
	editor: Editor<Attributes>;
	operation: UpdateOperation<Attributes, any> // eslint-disable-line @typescript-eslint/no-explicit-any
};
export type UpdateOperationView<Attributes> = (props: UpdateOperationViewProps<Attributes>) => JSX.Element;

export type UpdateOperationConfig<Attributes, Result> = CrudOperationConfig & {
	id: ResourceIdentifier;
	getAttributes: (api: ApiService) => Promise<Partial<Attributes>>;
	metadata: InputMetadata<Attributes>;
	mutation: TypedDocumentNode<Result, UpdateOperationMutationVariables<Attributes>>;
	view: UpdateOperationView<Attributes>;
};

export class UpdateOperation<Attributes, Result> extends CrudOperation<Result> {
	readonly #id: string;
	readonly #config: Readonly<UpdateOperationConfig<Attributes, Result>>;
	readonly editor: Editor<Attributes>;

	readonly view: () => JSX.Element;

	constructor(config: Readonly<UpdateOperationConfig<Attributes, Result>>) {
		super(config);
		this.#id = resourceId(config.id);
		this.#config = config;
		this.editor = new Editor({
			data: {},
			metadata: config.metadata,
		});

		this.view = () => createElement(config.view, { id: this.#id, editor: this.editor, operation: this });
	}

	async initialise(): Promise<void> {
		this.editor.resetData(await this.#config.getAttributes(this.services.api));
	}

	async submit({ refetchQueries }: CrudSubmitContext): Promise<CrudOperationResult<Result>> {
		if(!this.editor.changed) {
			return { status: "unchanged" };
		}

		const result = await this.services.api.mutation(this.#config.mutation, { refetchQueries })
			.submit({
				id: this.#id,
				attributes: this.editor.changedValues,
			}) as Result;

		return {
			status: "ok",
			value: result,
		};
	}

	dispose(): void { /**/ }

	override hasChanges(): boolean {
	  return this.editor.changed;
	}
}
